import React, { Component } from 'react';
import StatusBadge from './shared/StatusBadge';
import DetailRow from './shared/DetailRow';
import { formatDate } from './shared/formatters';
import AuthService from '../AuthService';
import constants from '../../config';

var Auth = new AuthService();

/**
 * Groups transactions that share a stripeChargeId or paypalOrderID.
 * Returns array of { type: 'single', tx } or { type: 'group', groupId, buyer, date, paymentMethod, items[], totalAmount, sellers, status }
 */
function groupTransactions(transactions) {
  var groupMap = {};
  var result = [];
  var order = [];

  transactions.forEach(function(tx) {
    var groupKey = null;
    if (tx.stripeChargeId) {
      groupKey = 'stripe_' + tx.stripeChargeId;
    } else if (tx.paypalOrderID) {
      groupKey = 'paypal_' + tx.paypalOrderID;
    }

    if (!groupKey) {
      result.push({ type: 'single', tx: tx });
      order.push({ type: 'single', index: result.length - 1 });
      return;
    }

    if (!groupMap[groupKey]) {
      groupMap[groupKey] = { items: [], firstIndex: order.length };
      order.push({ type: 'group', key: groupKey });
    }
    groupMap[groupKey].items.push(tx);
  });

  // Build final ordered array
  var finalRows = [];
  var processedGroups = {};

  order.forEach(function(entry) {
    if (entry.type === 'single') {
      finalRows.push(result[entry.index]);
    } else if (entry.type === 'group' && !processedGroups[entry.key]) {
      processedGroups[entry.key] = true;
      var group = groupMap[entry.key];
      if (group.items.length === 1) {
        // Only one item with this ID on this page — render as single
        finalRows.push({ type: 'single', tx: group.items[0] });
      } else {
        // Build group summary
        var items = group.items;
        var first = items[0];
        var totalAmount = 0;
        var sellerMap = {};
        var statuses = {};
        items.forEach(function(item) {
          totalAmount += (item.sellerPayout || 0);
          if (item.seller && item.seller.userName) {
            sellerMap[item.seller.userName] = true;
          }
          var s;
          if (item.cancelledAt) {
            s = 'refunded';
          } else if (item.partialRefundAmount > 0 && item.payoutStatus !== 'refunded') {
            s = 'partial_refund';
          } else {
            s = item.payoutStatus;
          }
          statuses[s] = true;
        });
        var statusKeys = Object.keys(statuses);
        var groupStatus = statusKeys.length === 1 ? statusKeys[0] : 'mixed';
        var sellers = Object.keys(sellerMap);

        finalRows.push({
          type: 'group',
          groupId: entry.key,
          buyer: first.buyer ? first.buyer.userName : (first.guestEmail || 'Guest'),
          date: first.createdAt,
          paymentMethod: first.paymentMethod,
          items: items,
          totalAmount: totalAmount,
          sellers: sellers,
          status: groupStatus,
        });
      }
    }
  });

  return finalRows;
}

function ActionButtons(props) {
  var tx = props.tx;
  var title = tx.post ? tx.post.title : 'this order';
  if (tx.payoutStatus !== 'held' && tx.payoutStatus !== 'disputed' && tx.payoutStatus !== 'payout_failed') return null;
  return (
    <div className="tw-flex tw-gap-1 tw-justify-center">
      {tx.payoutStatus === 'held' && (
        <button className="tw-text-xs tw-px-2 tw-py-1 tw-rounded-md tw-font-medium tw-border-0 tw-cursor-pointer hover:tw-opacity-80" style={{ backgroundColor: '#ECFDF5', color: '#047857' }} onClick={function(e) { e.stopPropagation(); props.onRelease(tx._id, title); }}>
          Release
        </button>
      )}
      {tx.payoutStatus === 'payout_failed' && (
        <button className="tw-text-xs tw-px-2 tw-py-1 tw-rounded-md tw-font-medium tw-border-0 tw-cursor-pointer hover:tw-opacity-80" style={{ backgroundColor: '#FFFBEB', color: '#92400E' }} onClick={function(e) { e.stopPropagation(); props.onRetryPayout(tx._id, title); }}>
          Retry
        </button>
      )}
      {tx.payoutStatus === 'payout_failed' && (
        <button className="tw-text-xs tw-px-2 tw-py-1 tw-rounded-md tw-font-medium tw-border-0 tw-cursor-pointer hover:tw-opacity-80" style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }} onClick={function(e) { e.stopPropagation(); props.onMarkReleased(tx._id, title); }}>
          Mark Released
        </button>
      )}
      {tx.payoutStatus !== 'payout_failed' && (
        <button className="tw-text-xs tw-px-2 tw-py-1 tw-rounded-md tw-font-medium tw-border-0 tw-cursor-pointer hover:tw-opacity-80" style={{ backgroundColor: '#FEF2F2', color: '#B91C1C' }} onClick={function(e) { e.stopPropagation(); props.onRefund(tx._id, title); }}>
          Refund
        </button>
      )}
    </div>
  );
}

function CartGroupRow(props) {
  var group = props.group;
  var isExpanded = props.isExpanded;
  var isDark = props.isDark;
  var itemCount = group.items.length;
  var sellersText = group.sellers.length <= 2 ? group.sellers.join(', ') : group.sellers[0] + ' +' + (group.sellers.length - 1);

  return (
    <tr
      className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] hover:tw-bg-[#f9f9f9] dark:hover:tw-bg-[#151515] tw-cursor-pointer tw-transition-colors"
      style={{ backgroundColor: isDark ? 'rgba(139, 92, 246, 0.05)' : 'rgba(139, 92, 246, 0.03)' }}
      onClick={props.onClick}
    >
      <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-whitespace-nowrap">{formatDate(group.date)}</td>
      <td className="tw-px-4 tw-py-3.5 tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold" style={{ backgroundColor: '#EDE9FE', color: '#6B21A8' }}>
            <i className="fa fa-shopping-cart" style={{ fontSize: 10 }} />
            {itemCount} items
          </span>
          <i className={'fa fa-chevron-' + (isExpanded ? 'up' : 'down') + ' tw-text-[10px] tw-text-[#999]'} />
        </div>
      </td>
      <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-max-w-[140px] tw-truncate">{group.buyer}</td>
      <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-max-w-[140px] tw-truncate">{sellersText || 'Unknown'}</td>
      <td className="tw-px-4 tw-py-3.5 tw-hidden lg:tw-table-cell"></td>
      <td className="tw-px-4 tw-py-3.5 tw-text-right tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${group.totalAmount.toFixed(2)}</td>
      <td className="tw-px-4 tw-py-3.5 tw-text-center">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
          <StatusBadge status={group.status} />
          {(function() {
            var shippedCount = group.items.filter(function(i) { return i.shippedAt; }).length;
            var total = group.items.length;
            if (shippedCount === total) return <span className="tw-text-[11px] tw-text-[#888] dark:tw-text-[#999] tw-whitespace-nowrap">All shipped</span>;
            if (shippedCount > 0) return <span className="tw-text-[11px] tw-text-[#888] dark:tw-text-[#999] tw-whitespace-nowrap">{shippedCount}/{total} shipped</span>;
            return <span className="tw-text-[11px] tw-text-[#bbb] dark:tw-text-[#666] tw-whitespace-nowrap">Not shipped</span>;
          })()}
        </div>
      </td>
      <td className="tw-px-4 tw-py-3.5 tw-text-center"></td>
    </tr>
  );
}

function TxDetail(props) {
  var tx = props.tx;
  var noteText = props.noteText;
  var partialAmount = props.partialAmount;
  var cancelReason = props.cancelReason;
  var actionLoading = props.actionLoading;
  var isDark = document.documentElement.classList.contains('tw-dark');

  return (
    <div className="tw-px-5 tw-py-4" style={{ backgroundColor: isDark ? '#111' : '#fafafa', borderTop: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-text-sm">
        <div>
          <h4 className="tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-m-0 tw-mb-3">Order Details</h4>
          <div className="tw-space-y-2">
            <DetailRow label="Order ID" value={tx._id} />
            <DetailRow label="Date" value={new Date(tx.createdAt).toLocaleString()} />
            <DetailRow label="Payment" value={tx.paymentMethod + (tx.cardBrand ? ' (' + tx.cardBrand + ' ...' + tx.cardLast4 + ')' : '')} />
            <DetailRow label="Seller Payout" value={'$' + (tx.sellerPayout || 0).toFixed(2)} />
            <DetailRow label="Seller Paid" value={tx.sellerPaid ? 'Yes' : 'No'} />
            <DetailRow label="Seller PayPal" value={tx.seller && tx.seller.paypalEmail ? tx.seller.paypalEmail : 'Not connected'} />
            <DetailRow label="Seller Stripe" value={tx.seller && tx.seller.stripeAccountId ? 'Connected' : 'Not connected'} />
            {tx.trackingNumber && <DetailRow label="Tracking" value={tx.trackingNumber + (tx.carrier ? ' (' + tx.carrier + ')' : '')} />}
            {tx.partialRefundAmount > 0 && <DetailRow label="Partial Refund" value={'$' + tx.partialRefundAmount.toFixed(2)} highlight />}
            {tx.cancelledAt && <DetailRow label="Cancelled" value={new Date(tx.cancelledAt).toLocaleString()} highlight />}
            {tx.cancelReason && <DetailRow label="Cancel Reason" value={tx.cancelReason} highlight />}
          </div>
        </div>
        <div>
          <h4 className="tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-m-0 tw-mb-3">Timeline</h4>
          <div className="tw-space-y-2">
            <DetailRow label="Created" value={new Date(tx.createdAt).toLocaleString()} />
            {tx.shippedAt && <DetailRow label="Shipped" value={new Date(tx.shippedAt).toLocaleString()} />}
            {tx.deliveredAt && <DetailRow label="Delivered" value={new Date(tx.deliveredAt).toLocaleString()} />}
            {tx.payoutScheduledAt && <DetailRow label="Auto-release" value={new Date(tx.payoutScheduledAt).toLocaleString()} />}
            {tx.disputedAt && <DetailRow label="Disputed" value={new Date(tx.disputedAt).toLocaleString()} highlight />}
            {tx.disputeReason && <DetailRow label="Reason" value={tx.disputeReason} highlight />}
            {tx.disputeResolvedAt && <DetailRow label="Resolved" value={new Date(tx.disputeResolvedAt).toLocaleString()} />}
            {tx.disputeResolution && <DetailRow label="Resolution" value={tx.disputeResolution} />}
            {tx.refundStatus && tx.refundStatus !== 'none' && <DetailRow label="Refund" value={tx.refundStatus + (tx.refundProviderId ? ' (' + tx.refundProviderId + ')' : '')} />}
          </div>
        </div>
      </div>

      {/* Admin Notes */}
      {tx.adminNotes && tx.adminNotes.length > 0 && (
        <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-[#e8e8e8]">
          <h4 className="tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-m-0 tw-mb-3">Admin Notes</h4>
          <div className="tw-space-y-2">
            {tx.adminNotes.map(function(n, i) {
              return (
                <div key={i} className="tw-p-2.5 tw-rounded-lg tw-border tw-border-[#eee] dark:tw-border-[#222]">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span className="tw-text-xs tw-font-semibold" style={{ color: '#D4A843' }}>{n.addedBy ? n.addedBy.userName : 'Admin'}</span>
                    <span className="tw-text-[10px] tw-text-[#999]">{n.createdAt ? formatDate(n.createdAt) : ''}</span>
                  </div>
                  <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-m-0">{n.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons Row */}
      {tx.payoutStatus !== 'released' && tx.payoutStatus !== 'refunded' && !tx.cancelledAt && (
        <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-[#e8e8e8]">
          <h4 className="tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-m-0 tw-mb-3">Actions</h4>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {tx.payoutStatus === 'payout_failed' && (
              <button
                className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
                style={{ backgroundColor: '#f59e0b' }}
                onClick={function() { props.onRetryPayout(tx._id, tx.post ? tx.post.title : 'this order'); }}
              >
                <i className="fa fa-refresh tw-mr-1" />Retry Payout
              </button>
            )}
            {tx.payoutStatus === 'payout_failed' && (
              <button
                className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
                style={{ backgroundColor: '#1D4ED8' }}
                onClick={function() { props.onMarkReleased(tx._id, tx.post ? tx.post.title : 'this order'); }}
              >
                <i className="fa fa-check-circle tw-mr-1" />Mark Released
              </button>
            )}
            {tx.payoutStatus === 'held' && (
              <button
                className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
                style={{ backgroundColor: '#10B981' }}
                onClick={function() { props.onForceComplete(tx._id, tx.post ? tx.post.title : 'this order'); }}
              >
                <i className="fa fa-forward tw-mr-1" />Force Complete
              </button>
            )}
            <button
              className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
              style={{ backgroundColor: '#EF4444' }}
              onClick={function() { props.onShowCancel(); }}
            >
              <i className="fa fa-times-circle tw-mr-1" />Cancel Order
            </button>
            <button
              className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
              style={{ backgroundColor: '#f59e0b' }}
              onClick={function() { props.onShowPartial(); }}
            >
              <i className="fa fa-minus-circle tw-mr-1" />Partial Refund
            </button>
          </div>

          {/* Cancel Form */}
          {props.showCancel && (
            <div className="tw-mt-3 tw-p-3 tw-rounded-lg tw-border tw-border-[#eee] dark:tw-border-[#222]">
              <label className="tw-text-xs tw-text-[#666] tw-block tw-mb-1">Cancel Reason</label>
              <input
                type="text"
                className="tw-w-full tw-px-4 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-mb-2"
                placeholder="Reason for cancellation (optional)"
                value={cancelReason}
                onChange={function(e) { props.onCancelReasonChange(e.target.value); }}
              />
              <button
                className="tw-text-xs tw-px-4 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
                style={{ backgroundColor: '#EF4444' }}
                disabled={actionLoading}
                onClick={function() { props.onCancelOrder(tx._id); }}
              >
                {actionLoading ? <i className="fa fa-spinner fa-spin" /> : 'Confirm Cancel & Refund'}
              </button>
            </div>
          )}

          {/* Partial Refund Form */}
          {props.showPartial && (
            <div className="tw-mt-3 tw-p-3 tw-rounded-lg tw-border tw-border-[#eee] dark:tw-border-[#222]">
              {(function() {
                var sellerPayout = Number(tx.sellerPayout || 0);
                var alreadyRefunded = tx.partialRefundAmount || 0;
                var maxRefund = Math.max(0, Math.round((sellerPayout - alreadyRefunded) * 100) / 100);
                var enteredAmount = parseFloat(partialAmount) || 0;
                var exceedsMax = enteredAmount > maxRefund;
                return (
                  <div>
                    <label className="tw-text-xs tw-text-[#666] tw-block tw-mb-1">
                      Refund Amount ($)
                      {sellerPayout > 0 && <span className="tw-text-[#999] tw-ml-1">(max: ${maxRefund.toFixed(2)})</span>}
                    </label>
                    <div className="tw-flex tw-items-center" style={{ gap: 10 }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={maxRefund > 0 ? maxRefund : undefined}
                        className={'tw-px-4 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none tw-transition-colors ' + (exceedsMax ? 'tw-border-red-500 focus:tw-border-red-500' : 'tw-border-[#d0d0d0] dark:tw-border-[#555] focus:tw-border-brand-gold')}
                        style={{ width: 160 }}
                        placeholder="0.00"
                        value={partialAmount}
                        onChange={function(e) { props.onPartialAmountChange(e.target.value); }}
                      />
                      <button
                        className="tw-text-xs tw-px-4 tw-py-2.5 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-whitespace-nowrap"
                        style={{ backgroundColor: (actionLoading || !partialAmount || enteredAmount <= 0 || exceedsMax) ? '#999' : '#f59e0b', cursor: (actionLoading || !partialAmount || enteredAmount <= 0 || exceedsMax) ? 'not-allowed' : 'pointer' }}
                      disabled={actionLoading || !partialAmount || enteredAmount <= 0 || exceedsMax}
                      onClick={function() { props.onPartialRefund(tx._id); }}
                    >
                        {actionLoading ? <i className="fa fa-spinner fa-spin" /> : 'Issue Partial Refund'}
                      </button>
                    </div>
                    {exceedsMax && <p className="tw-text-xs tw-text-red-500 tw-m-0" style={{ marginTop: 6 }}>Amount exceeds remaining balance of ${maxRefund.toFixed(2)}</p>}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Add Note */}
      <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-[#e8e8e8]">
        <h4 className="tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-m-0 tw-mb-2">Add Note</h4>
        <div className="tw-flex tw-gap-2">
          <input
            type="text"
            className="tw-flex-1 tw-px-4 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold tw-transition-colors"
            placeholder="Add a note to this order..."
            value={noteText}
            onChange={function(e) { props.onNoteChange(e.target.value); }}
          />
          <button
            className="tw-text-xs tw-px-4 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
            style={{ backgroundColor: '#3B82F6' }}
            disabled={actionLoading || !noteText.trim()}
            onClick={function() { props.onAddNote(tx._id); }}
          >
            {actionLoading ? <i className="fa fa-spinner fa-spin" /> : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderTxDetailProps(self, tx) {
  return {
    tx: tx,
    noteText: self.state.noteText,
    partialAmount: self.state.partialAmount,
    cancelReason: self.state.cancelReason,
    actionLoading: self.state.actionLoading,
    showCancel: self.state.showCancel,
    showPartial: self.state.showPartial,
    onShowCancel: function() { self.setState({ showCancel: true, showPartial: false }); },
    onShowPartial: function() { self.setState({ showPartial: true, showCancel: false }); },
    onNoteChange: function(v) { self.setState({ noteText: v }); },
    onPartialAmountChange: function(v) { self.setState({ partialAmount: v }); },
    onCancelReasonChange: function(v) { self.setState({ cancelReason: v }); },
    onRetryPayout: function(id, title) {
      self.props.onConfirm({
        title: 'Retry Payout',
        message: 'Retry payout for "' + title + '"? This will attempt to send funds to the seller.',
        btnColor: '#f59e0b',
        btnIcon: 'fa-refresh',
        onConfirm: function() {
          return Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + id + '/retry-payout', { method: 'POST' })
            .then(function() { self.fetchTransactions(self.state.pagination.page); });
        },
      });
    },
    onMarkReleased: function(id, title) {
      self.props.onConfirm({
        title: 'Mark as Released',
        message: 'Mark "' + title + '" as released? Use this only if the payout was sent manually outside the platform.',
        btnColor: '#1D4ED8',
        btnIcon: 'fa-check-circle',
        onConfirm: function() {
          return Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + id + '/mark-released', { method: 'POST' })
            .then(function() { self.fetchTransactions(self.state.pagination.page); });
        },
      });
    },
    onForceComplete: function(id, title) {
      self.props.onConfirm({
        title: 'Force Complete Order',
        message: 'Force complete "' + title + '"? This will release the payout to the seller immediately.',
        btnColor: '#10B981',
        btnIcon: 'fa-forward',
        onConfirm: function() {
          return Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + id + '/force-complete', { method: 'POST' })
            .then(function() { self.fetchTransactions(self.state.pagination.page); });
        },
      });
    },
    onCancelOrder: function(id) { self.handleCancelOrder(id); },
    onPartialRefund: function(id) { self.handlePartialRefund(id); },
    onAddNote: function(id) { self.handleAddNote(id); },
  };
}

function renderActionButtonsProps(self, tx) {
  return {
    tx: tx,
    onRelease: function(id, title) { self.props.onConfirm({ type: 'release', txId: id, title: title }); },
    onRefund: function(id, title) { self.props.onConfirm({ type: 'refund', txId: id, title: title }); },
    onRetryPayout: function(id, title) {
      self.props.onConfirm({
        title: 'Retry Payout',
        message: 'Retry payout for "' + title + '"? This will attempt to send funds to the seller.',
        btnColor: '#f59e0b',
        btnIcon: 'fa-refresh',
        onConfirm: function() {
          return Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + id + '/retry-payout', { method: 'POST' })
            .then(function() { self.fetchTransactions(self.state.pagination.page); });
        },
      });
    },
    onMarkReleased: function(id, title) {
      self.props.onConfirm({
        title: 'Mark as Released',
        message: 'Mark "' + title + '" as released? Use this only if the payout was sent manually outside the platform.',
        btnColor: '#1D4ED8',
        btnIcon: 'fa-check-circle',
        onConfirm: function() {
          return Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + id + '/mark-released', { method: 'POST' })
            .then(function() { self.fetchTransactions(self.state.pagination.page); });
        },
      });
    },
  };
}

function PayoutMethodCell(props) {
  var tx = props.tx;
  return (
    <td className="tw-px-4 tw-py-3.5 tw-hidden lg:tw-table-cell">
      <div className="tw-flex tw-flex-col tw-gap-1">
        {tx.seller && tx.seller.paypalEmail && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium" style={{ backgroundColor: '#FFF7ED', color: '#C2410C', border: '1px solid #FDBA74' }}>
            <i className="fa fa-paypal" style={{ fontSize: 10 }} /> {tx.seller.paypalEmail}
          </span>
        )}
        {tx.seller && tx.seller.stripeAccountId && (
          <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium" style={{ backgroundColor: '#EEF2FF', color: '#4338CA', border: '1px solid #A5B4FC' }}>
            <i className="fa fa-cc-stripe" style={{ fontSize: 10 }} /> Connected
          </span>
        )}
        {tx.seller && !tx.seller.paypalEmail && !tx.seller.stripeAccountId && (
          <span className="tw-text-[11px] tw-text-[#999]">None</span>
        )}
      </div>
    </td>
  );
}

class AdminTransactions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      transactions: [],
      groupedRows: [],
      pagination: { page: 1, limit: 20, total: 0, pages: 0 },
      filters: { status: '', paymentMethod: '', search: '' },
      expandedTxId: null,
      expandedGroupId: null,
      loading: true,
      noteText: '',
      partialAmount: '',
      cancelReason: '',
      showCancel: false,
      showPartial: false,
      actionLoading: false,
    };
    this.fetchTransactions = this.fetchTransactions.bind(this);
    this.handleCancelOrder = this.handleCancelOrder.bind(this);
    this.handlePartialRefund = this.handlePartialRefund.bind(this);
    this.handleAddNote = this.handleAddNote.bind(this);
    this.handleRetryPayout = this.handleRetryPayout.bind(this);
  }

  componentDidMount() {
    this.fetchTransactions(1);
  }

  componentDidUpdate(prevProps) {
    if (this.props.refreshKey !== prevProps.refreshKey) {
      this.fetchTransactions(this.state.pagination.page);
    }
  }

  fetchTransactions(page) {
    var self = this;
    var filters = self.state.filters;
    var params = new URLSearchParams();
    params.append('page', page || 1);
    params.append('limit', 20);
    if (filters.status) params.append('status', filters.status);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.search) params.append('search', filters.search);
    self.setState({ loading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions?' + params.toString())
      .then(function(data) {
        var grouped = groupTransactions(data.transactions);
        self.setState({
          transactions: data.transactions,
          groupedRows: grouped,
          pagination: data.pagination,
          loading: false,
          expandedTxId: null,
          expandedGroupId: null,
        });
      })
      .catch(function(err) { console.log('Failed to fetch transactions:', err); self.setState({ loading: false }); });
  }

  handleCancelOrder(txId) {
    var self = this;
    self.setState({ actionLoading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + txId + '/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: self.state.cancelReason }),
    })
      .then(function() {
        self.setState({ actionLoading: false, cancelReason: '', showCancel: false });
        self.fetchTransactions(self.state.pagination.page);
      })
      .catch(function(err) { self.setState({ actionLoading: false }); alert('Cancel failed: ' + (err.message || 'Unknown error')); });
  }

  handlePartialRefund(txId) {
    var self = this;
    self.setState({ actionLoading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + txId + '/partial-refund', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: parseFloat(self.state.partialAmount) }),
    })
      .then(function() {
        self.setState({ actionLoading: false, partialAmount: '', showPartial: false });
        self.fetchTransactions(self.state.pagination.page);
      })
      .catch(function(err) { self.setState({ actionLoading: false }); alert('Partial refund failed: ' + (err.message || 'Unknown error')); });
  }

  handleRetryPayout(txId) {
    var self = this;
    self.setState({ actionLoading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + txId + '/retry-payout', {
      method: 'POST',
    })
      .then(function() {
        self.setState({ actionLoading: false });
        self.fetchTransactions(self.state.pagination.page);
      })
      .catch(function(err) { self.setState({ actionLoading: false }); alert('Retry payout failed: ' + (err.message || 'Unknown error')); });
  }

  handleAddNote(txId) {
    var self = this;
    self.setState({ actionLoading: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/transactions/' + txId + '/note', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: self.state.noteText }),
    })
      .then(function() {
        self.setState({ actionLoading: false, noteText: '' });
        self.fetchTransactions(self.state.pagination.page);
      })
      .catch(function(err) { self.setState({ actionLoading: false }); alert('Failed to add note: ' + (err.message || 'Unknown error')); });
  }

  render() {
    var self = this;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var groupedRows = this.state.groupedRows;
    var pagination = this.state.pagination;
    var filters = this.state.filters;

    if (this.state.loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
          <div className="tw-text-center">
            <i className="fa fa-circle-o-notch fa-spin tw-text-3xl tw-mb-3" style={{ color: '#D4A843', display: 'block' }} />
            <span className="tw-text-sm tw-text-[#999]">Loading...</span>
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* Filter Bar */}
        <div className="tw-rounded-xl tw-p-4 tw-mb-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-center">
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white"
              style={isDark ? { colorScheme: 'dark' } : {}}
              value={filters.status}
              onChange={function(e) { var f = Object.assign({}, filters, { status: e.target.value }); self.setState({ filters: f, expandedTxId: null, expandedGroupId: null }, function() { self.fetchTransactions(1); }); }}
            >
              <option value="">All Statuses</option>
              <option value="held">Held</option>
              <option value="released">Released</option>
              <option value="disputed">Disputed</option>
              <option value="refunded">Refunded</option>
              <option value="payout_failed">Payout Failed</option>
              <option value="uncaptured">Uncaptured PayPal</option>
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white"
              style={isDark ? { colorScheme: 'dark' } : {}}
              value={filters.paymentMethod}
              onChange={function(e) { var f = Object.assign({}, filters, { paymentMethod: e.target.value }); self.setState({ filters: f, expandedTxId: null, expandedGroupId: null }, function() { self.fetchTransactions(1); }); }}
            >
              <option value="">All Methods</option>
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
            </select>
            <div className="tw-flex tw-flex-1 tw-min-w-0" style={{ minWidth: 200 }}>
              <input
                type="text"
                className="tw-flex-1 tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-l-lg tw-px-3 tw-py-2.5 tw-text-sm tw-outline-none focus:tw-border-brand-gold tw-transition-colors tw-min-w-0 tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white"
                placeholder="Search by ID or username..."
                value={filters.search}
                onChange={function(e) { self.setState({ filters: Object.assign({}, filters, { search: e.target.value }) }); }}
                onKeyDown={function(e) { if (e.key === 'Enter') self.fetchTransactions(1); }}
              />
              <button
                className="tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-black tw-rounded-r-lg tw-border-0 tw-cursor-pointer"
                style={{ backgroundColor: '#D4A843' }}
                onClick={function() { self.fetchTransactions(1); }}
              >
                <i className="fa fa-search" />
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="tw-rounded-xl tw-overflow-hidden" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm" style={{ minWidth: '600px' }}>
              <thead>
                <tr style={{ backgroundColor: isDark ? '#111' : '#fafafa', borderBottom: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
                  <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Date</th>
                  <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Item</th>
                  <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Buyer</th>
                  <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Seller</th>
                  <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden lg:tw-table-cell">Payout Method</th>
                  <th className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Amount</th>
                  <th className="tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Status</th>
                  <th className="tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedRows.map(function(row, rowIdx) {
                  if (row.type === 'single') {
                    var tx = row.tx;
                    var isExpanded = self.state.expandedTxId === tx._id;
                    return (
                      <React.Fragment key={tx._id}>
                        <tr
                          className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] hover:tw-bg-[#f9f9f9] dark:hover:tw-bg-[#151515] tw-cursor-pointer tw-transition-colors"
                          onClick={function() { self.setState({ expandedTxId: isExpanded ? null : tx._id, showCancel: false, showPartial: false, noteText: '', partialAmount: '', cancelReason: '' }); }}
                        >
                          <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-whitespace-nowrap">{formatDate(tx.createdAt)}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-max-w-xs tw-truncate">{tx.post ? tx.post.title : 'Deleted'}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-max-w-[140px] tw-truncate">{tx.buyer ? tx.buyer.userName : (tx.guestEmail || 'Guest')}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-[#666] dark:tw-text-[#aaa] tw-max-w-[140px] tw-truncate">{tx.seller ? tx.seller.userName : 'Unknown'}</td>
                          <PayoutMethodCell tx={tx} />
                          <td className="tw-px-4 tw-py-3.5 tw-text-right tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${(tx.sellerPayout || 0).toFixed(2)}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-center">
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                              <StatusBadge status={tx.partialRefundAmount > 0 && tx.payoutStatus !== 'refunded' ? 'partial_refund' : tx.payoutStatus} label={tx.partialRefundAmount > 0 && tx.payoutStatus !== 'refunded' ? 'partial refund' : undefined} />
                              {tx.shippedAt
                                ? <span className="tw-text-[11px] tw-text-[#888] dark:tw-text-[#999] tw-whitespace-nowrap">Shipped {formatDate(tx.shippedAt)}</span>
                                : <span className="tw-text-[11px] tw-text-[#bbb] dark:tw-text-[#666] tw-whitespace-nowrap">Not shipped</span>}
                            </div>
                          </td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-center">
                            <ActionButtons {...renderActionButtonsProps(self, tx)} />
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr><td colSpan="8" className="tw-p-0">
                            <TxDetail {...renderTxDetailProps(self, tx)} />
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  // Group row
                  var group = row;
                  var isGroupExpanded = self.state.expandedGroupId === group.groupId;
                  return (
                    <React.Fragment key={group.groupId}>
                      <CartGroupRow
                        group={group}
                        isExpanded={isGroupExpanded}
                        isDark={isDark}
                        onClick={function() {
                          self.setState({
                            expandedGroupId: isGroupExpanded ? null : group.groupId,
                            expandedTxId: null,
                            showCancel: false,
                            showPartial: false,
                            noteText: '',
                            partialAmount: '',
                            cancelReason: '',
                          });
                        }}
                      />
                      {isGroupExpanded && (
                        <tr><td colSpan="8" className="tw-p-0">
                          <div className="tw-px-3 tw-py-2" style={{ backgroundColor: isDark ? '#0d0d0d' : '#f5f3ff', borderTop: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
                            <table className="tw-w-full tw-text-sm">
                              <tbody>
                                {group.items.map(function(tx) {
                                  var isItemExpanded = self.state.expandedTxId === tx._id;
                                  return (
                                    <React.Fragment key={tx._id}>
                                      <tr
                                        className="tw-border-b tw-border-[#e8e8e8] dark:tw-border-[#222] hover:tw-bg-[#f0ecff] dark:hover:tw-bg-[#1a1a2e] tw-cursor-pointer tw-transition-colors"
                                        onClick={function() { self.setState({ expandedTxId: isItemExpanded ? null : tx._id, showCancel: false, showPartial: false, noteText: '', partialAmount: '', cancelReason: '' }); }}
                                      >
                                        <td className="tw-px-4 tw-py-3 tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-max-w-xs tw-truncate">{tx.post ? tx.post.title : 'Deleted'}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-[#666] dark:tw-text-[#aaa] tw-max-w-[140px] tw-truncate">{tx.seller ? tx.seller.userName : 'Unknown'}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white">${(tx.sellerPayout || 0).toFixed(2)}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-center">
                                          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                                            <StatusBadge status={tx.partialRefundAmount > 0 && tx.payoutStatus !== 'refunded' ? 'partial_refund' : tx.payoutStatus} label={tx.partialRefundAmount > 0 && tx.payoutStatus !== 'refunded' ? 'partial refund' : undefined} />
                                            {tx.shippedAt
                                              ? <span className="tw-text-[11px] tw-text-[#888] dark:tw-text-[#999] tw-whitespace-nowrap">Shipped {formatDate(tx.shippedAt)}</span>
                                              : <span className="tw-text-[11px] tw-text-[#bbb] dark:tw-text-[#666] tw-whitespace-nowrap">Not shipped</span>}
                                          </div>
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-center">
                                          <ActionButtons {...renderActionButtonsProps(self, tx)} />
                                        </td>
                                      </tr>
                                      {isItemExpanded && (
                                        <tr><td colSpan="5" className="tw-p-0">
                                          <TxDetail {...renderTxDetailProps(self, tx)} />
                                        </td></tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {groupedRows.length === 0 && (
                  <tr><td colSpan="8" className="tw-px-4 tw-py-12 tw-text-center tw-text-[#999]">
                    <i className="fa fa-inbox tw-text-3xl tw-mb-2" style={{ display: 'block' }} />No transactions found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.pages > 1 && (
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-3 tw-border-t tw-border-[#e8e8e8]">
              <span className="tw-text-xs tw-text-[#999]">Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</span>
              <div className="tw-flex tw-gap-1">
                <button className="tw-px-3 tw-py-1.5 tw-text-xs tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer disabled:tw-opacity-40 tw-transition-colors" disabled={pagination.page <= 1} onClick={function() { self.fetchTransactions(pagination.page - 1); }}>Prev</button>
                <button className="tw-px-3 tw-py-1.5 tw-text-xs tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer disabled:tw-opacity-40 tw-transition-colors" disabled={pagination.page >= pagination.pages} onClick={function() { self.fetchTransactions(pagination.page + 1); }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AdminTransactions;
